import { Box, BoxProps, SystemStyleObject } from "@chakra-ui/react"
import { ComponentType } from "react"
import {
  AUGMENTATION_PANEL_ACTION_BUTTON_PROPS,
  AUGMENTATION_PANEL_CHECK_CONTROL_PROPS,
  AUGMENTATION_PANEL_INPUT_PROPS,
  AUGMENTATION_PANEL_SELECT_PROPS,
  AUGMENTATION_PANEL_STATIC_TEXT_CSS,
  AUGMENTATION_PANEL_SUBMIT_BUTTON_PROPS
} from "./augmentationPanelStyles"

/**
 * Panel dress for the legacy settings forms embedded in a panel — today the
 * cohort and license offering forms that Cover Page offers reach through the
 * `window` toolbar bridge.
 *
 * This is a stop-gap with a deliberate shape: it exists so those forms are
 * usable at panel width before they're rebuilt as native panel sections, and it
 * is meant to be deleted whole rather than grown. Prefer a native section over
 * adding to it.
 *
 * Why a wrapper rather than rules under `.AugmentationPanel`: the same forms
 * render on the `/manage/group/…` and license Card routes, where they must not
 * change. Wrapping them at the toolbar nodes — which only the panel reads —
 * makes that scope structural rather than something a future selector could
 * widen by accident.
 */

const LEGACY_FORM_CSS: SystemStyleObject = {
  // Gutters. Legacy forms and field rows each carry their own page gutter on top
  // of the panel's, which at panel width leaves less room for the field than for
  // the space around it. The panel already insets its content, so everything
  // here runs to the same edge — a section of radio rows inset 20px inside a
  // full-width input and a full-width button is the tell that it doesn't belong.
  //
  // Doubled `&&` because the gutters come from styled-components at one class,
  // which a single-class scope only ties with.
  "&& :where(form, .FieldWrapper, .FieldFooter, .FieldHeader, .View)": {
    paddingInline: 0
  },

  // Vertical rhythm. Legacy stacks 20px section padding on a 20px header pad on
  // a 9px label pad, which at panel width reads as drift rather than grouping.
  // Restated on the panel's two intervals: 15px between sections, 2px between a
  // label and what it labels.
  "& .View.with-top-border": {
    paddingBlock: "augmentationPanel"
  },
  "& :where(.FieldWrapper, .UIFieldWrapper)": {
    paddingBlock: 0
  },
  "& :where(.View) > header": {
    paddingBottom: "2px",
    paddingTop: 0
  },

  // Label columns, the reason these forms overflow the panel. `labelWidth` is an
  // inline 110–240px sized for a full-width Card. The legacy CSS already has the
  // narrow answer — `FieldWrapper.css` stacks the label above the field — but it
  // keys off the *viewport* at 480px, and the panel is a 300px container in a
  // full-size window. So this applies the same treatment, keyed to the panel
  // instead. `!important` is what it takes to beat an inline width, which is why
  // the 480px rule uses it too.
  //
  // Legacy has two field systems, and the row lives at a different level in
  // each: `.FieldWrapper` is itself the flex row, while `.UIFieldWrapper` is a
  // container whose row is an unclassed child — matched here by the label it
  // holds.
  //
  // Inline field wrappers are left alone: they're the small number inputs sitting
  // inside a sentence ("Charge $ ⬚ per seat"), which stacking would break apart.
  // Toggle rows are left alone too — a short label beside a switch reads better
  // as a row, and it's the shape the panel's own settings rows already use.
  "& :where(.FieldWrapper:not(.FieldWrapper--inline), .UIFieldWrapper:not(.ToggleInput) > *:has(> .UIFieldWrapper__label-wrapper))":
    {
      alignItems: "stretch",
      flexDirection: "column",
      gap: "2px"
    },
  // Those toggle rows do need their switch pushed to the far edge, though.
  // Legacy leaves it wherever the label column happens to end, which lands it
  // against the label — the panel's own settings rows put the control at the
  // right edge with the label reading toward it.
  "& .ToggleInput > *:has(> .UIFieldWrapper__label-wrapper)": {
    gap: "augmentationPanelGutter"
  },
  "& .ToggleInput .ToggleInput__switch": {
    justifyContent: "flex-end"
  },
  "& :where(.FieldWrapper__label, .UIFieldWrapper__label, .UIFieldWrapper__label-wrapper)":
    {
      maxWidth: "100%",
      padding: 0,
      width: "auto !important"
    },

  // Type. Legacy labels are 14px at 50% gray, and its status and footer copy set
  // their own sizes.
  "& :where(.FieldWrapper__label, .UIFieldWrapper__label)":
    AUGMENTATION_PANEL_STATIC_TEXT_CSS,
  "& :where(.FieldWrapper__status, .FieldWrapper__custom, .FieldFooter)": {
    ...AUGMENTATION_PANEL_STATIC_TEXT_CSS,
    fontSize: "12px"
  },
  // Legacy states a section's question as a heading rather than a label, but in
  // the panel it does a label's job — "Who can discover & join this cohort?"
  // sitting over its options is the same thing as "Cohort Name" over its input,
  // so it's stated the same way. `CardLink` headings are restated below.
  "&& :where(h1, h2, h3, h4, h5, h6)": AUGMENTATION_PANEL_STATIC_TEXT_CSS,

  // The value side of a field carries loose copy the panel has no hook for —
  // the "join" trailing "Close registration after ⬚", the words between the
  // inputs in "Charge $ ⬚ per seat". Stating the type once on the container
  // catches all of it, including anything sized in `em` off it.
  "& :where(.FieldWrapper__input, .UIFieldWrapper__input)": {
    textStyle: "augmentationPanel"
  },

  // Controls. Legacy text inputs are a 1px #ddd box at a 4px radius; the panel's
  // are a filled pill. Checkboxes and radios keep their own control.
  //
  // `:is()` rather than `:where()` on the control: legacy states these as
  // `.FieldStyler__input input`, and a zero-specificity match loses to it —
  // which is how these inputs kept a legacy border while taking the panel's
  // height.
  //
  // The fill and the inset are forced because a second legacy rule reaches the
  // same inputs from a styled-component, at a class plus three `:not()`s — a
  // specificity a scoped rule can't practically climb to. Those two are the
  // only properties it sets that the panel needs back. `FieldWrapper.css`
  // forces its own narrow layout for the same reason.
  "& :where(.FieldWrapper__input, .UIFieldWrapper__input) :is(input:not([type='checkbox'], [type='radio'], [role='combobox']), textarea, select)":
    {
      ...AUGMENTATION_PANEL_INPUT_PROPS,
      background:
        "var(--chakra-colors-augmentation-panel-fill-muted) !important",
      border: "none",
      paddingInline:
        "var(--chakra-spacing-augmentation-panel-gutter) !important"
    },

  // Selection controls. Legacy leaves radios in Chakra's defaults — 16px/400
  // text beside a 20px blue dot — which reads as another form dropped into the
  // panel. The panel's own check control was written to be worn by any Chakra
  // checkmark, so the radio wears it here. The text is restated rather than
  // inherited because the item sets its own weight inline.
  "& [data-scope='radio-group'][data-part='item-control']":
    AUGMENTATION_PANEL_CHECK_CONTROL_PROPS,
  "& [data-scope='radio-group'][data-part='item-text']": {
    textStyle: "augmentationPanel"
  },

  // These forms carry a second radio system — legacy `.RadioButton` around a
  // native input — which would have to be redrawn from scratch to wear the
  // control above. Tinting is the standards-level way to at least state it in
  // the panel's ink rather than the browser's blue.
  "& :where(input[type='radio'], input[type='checkbox'])": {
    accentColor: "augmentationPanel.text"
  },

  // Those radios come as filled rows, which is a shape the panel already has —
  // so they're stated on the panel's row geometry and sit 2px apart like a panel
  // list, rather than the 10px that reads as three separate things.
  "& label.RadioButton": {
    borderRadius: "augmentationPanel",
    cursor: "pointer",
    marginBlock: 0,
    paddingBlock: "augmentationPanelGutter",
    paddingInline: "augmentationPanelGutter"
  },
  // A panel row of this shape answers to the pointer, and these are clickable
  // across their whole width — nothing else here said so. Written as a real
  // selector because a nested `_hover` inside a selector key is dropped rather
  // than expanded.
  "& label.RadioButton:hover": {
    background: "augmentationPanel.fill.muted",
    cursor: "pointer"
  },
  "& label.RadioButton + label.RadioButton": {
    marginTop: "2px"
  },

  // The legacy toggle turns brand red when on, which is the one saturated thing
  // in a panel stated entirely in black alpha. Nothing in its markup says which
  // state it's in — the classes are identical either way — so the checked state
  // is read off the input it wraps.
  "& .ToggleInput .Switch": {
    backgroundColor: "augmentationPanel.fill.bold"
  },
  "& .ToggleInput .Switch:has(input:checked)": {
    backgroundColor: "augmentationPanel.text"
  },

  // Legacy selects are `react-select`, which hashes its own class names, so its
  // parts are matched by the suffix convention it appends to each one.
  "& [class$='-container'] [class$='-control']": {
    ...AUGMENTATION_PANEL_SELECT_PROPS,
    border: "none",
    boxShadow: "none",
    h: "auto",
    minH: "augmentationPanelRow"
  },
  "& [class$='-container'] [class$='-indicatorSeparator']": {
    display: "none"
  },
  // A date field's calendar button is absolutely positioned in a 30px slot at
  // the field's left edge, so its text starts after it rather than under it.
  "& .DatePicker__input input": {
    paddingLeft: "30px !important"
  },

  // `react-select` hides a dummy input inside its control. Dressing that as a
  // field puts a second pill inside the one you can see, so it's handed back.
  // Scoped to the select rather than excluded by `[readonly]`, which would also
  // skip the date inputs — those are readonly because they open a picker.
  "& [class$='-container'] input": {
    background: "transparent !important",
    height: "auto",
    paddingInline: "0 !important"
  },
  // The chevron ships at #ccc, which read on the white control it was drawn for
  // but disappears on the panel's filled one.
  "& [class$='-container'] [class$='-indicatorContainer']": {
    color: "augmentationPanel.text",
    opacity: 0.6,
    p: 0
  },
  "& [class$='-container'] [class$='-menu']": {
    borderRadius: "augmentationPanel",
    boxShadow: "augmentationPanel",
    overflow: "hidden"
  },

  // `CardLink` rows navigate to a sub-panel, so they're stated as panel rows.
  // Matched by Chakra's `LinkBox` class, which is what `CardLink` roots itself in.
  "& .chakra-linkbox": {
    bg: "augmentationPanel.fill.subtle",
    borderRadius: "augmentationPanel",
    overflow: "hidden"
  },
  "& .chakra-linkbox:hover": {
    bg: "augmentationPanel.fill.muted"
  },
  "& .chakra-linkbox .View": {
    borderTop: "none",
    paddingBlock: 1,
    paddingInline: "augmentationPanelGutter"
  },
  // A `CardLink` states its label as a heading and its description as meta text,
  // both a step up in weight from a panel row.
  "& .chakra-linkbox :where(h1, h2, h3, h4, h5, h6)": {
    textStyle: "augmentationPanel"
  },
  "& .chakra-linkbox label > * + *": {
    ...AUGMENTATION_PANEL_STATIC_TEXT_CSS,
    fontSize: "12px"
  },

  // Submit. The legacy button is branded and centered mid-form; in a panel it's
  // the section's one action, so it reads as the panel's submit.
  "& .SubmitStatusButton": {
    width: "100%"
  },
  "& .SubmitButton": {
    ...AUGMENTATION_PANEL_ACTION_BUTTON_PROPS,
    ...AUGMENTATION_PANEL_SUBMIT_BUTTON_PROPS,
    width: "100%"
  },
  "& .SelectRole .Select__option.Select__option--is-selected h5": {
    color: "inherit !important",
    fontWeight: "600 !important"
  }
}

const CARD_FIELD_PADDING_CSS: SystemStyleObject = {
  // Home panel wraps of Card forms need the Card's field rhythm back — the base
  // rule zeroes field padding so Cover Page cohort/license forms don't carry
  // page gutters into the panel.
  "& :where(.FieldWrapper, .UIFieldWrapper)": {
    p: "30px 0px 5px"
  }
}

export type AugmentationPanelLegacyFormProps = BoxProps & {
  /**
   * Restore Card-style FieldWrapper padding. Use for Home panel destinations
   * that embed the same forms as Card routes; leave off for Cover Page legacy
   * offer manage forms, which want the base zeroed field padding.
   */
  cardFieldPadding?: boolean
}

export function AugmentationPanelLegacyForm({
  children,
  css,
  cardFieldPadding = false,
  ...props
}: AugmentationPanelLegacyFormProps) {
  return (
    <Box
      className="AugmentationPanelLegacyForm"
      css={[
        LEGACY_FORM_CSS,
        cardFieldPadding ? CARD_FIELD_PADDING_CSS : undefined,
        css
      ]}
      // Legacy sizes much of itself in `em` off whatever it inherits — the
      // submit button's inline `font-size: 1em` is the loudest — so setting the
      // panel's scale as the base lands those on it without chasing each one.
      textStyle="augmentationPanel"
      w="100%"
      {...props}
    >
      {children}
    </Box>
  )
}

/**
 * Wraps a legacy component for use as a toolbar node's `AugmentationComponent`,
 * where there's a component reference rather than an element to wrap.
 */
export function withAugmentationPanelLegacyForm<P extends object>(
  Component: ComponentType<P>
) {
  function WithAugmentationPanelLegacyForm(props: P) {
    return (
      <AugmentationPanelLegacyForm>
        <Component {...props} />
      </AugmentationPanelLegacyForm>
    )
  }

  WithAugmentationPanelLegacyForm.displayName = `withAugmentationPanelLegacyForm(${
    Component.displayName || Component.name || "Component"
  })`

  return WithAugmentationPanelLegacyForm
}
